import { Question, Response } from '../types';
import { getScoringRulesMap } from '../utils/questionUtils';
import { MaturityAnalysis } from './maturityScoringService';

export interface TagBasedAssessment {
  tag: string;
  displayName: string;
  description: string;
  analysis: MaturityAnalysis;
  questionCount: number;
  answeredCount: number;
}

class TagBasedScoringService {
  /**
   * Calculate tag-based assessments (e.g., AI Readiness)
   */
  calculateTagBasedAssessments(
    questions: Question[],
    responses: Response[],
    tags: string[] = ['AI']
  ): TagBasedAssessment[] {
    return tags.map(tag => {
      // Filter questions that have this tag
      const tagQuestions = questions.filter(q => {
        if (!q.tags || !Array.isArray(q.tags)) return false;
        return q.tags.some(t =>
          (typeof t === 'string' && t.toLowerCase().includes(tag.toLowerCase())) ||
          (tag === 'AI' && q.isAiQuestion === true)
        );
      });

      // Filter responses for these questions
      const tagResponses = responses.filter(r =>
        tagQuestions.some(q => q.id === r.questionId)
      );

      // Calculate scores using the same logic as dimension scoring
      const analysis = this.calculateTagAnalysis(tagQuestions, tagResponses);

      // Get display name
      const displayName = this.getDisplayName(tag);
      const description = this.getDescription(tag);

      return {
        tag,
        displayName,
        description,
        analysis,
        questionCount: tagQuestions.length,
        answeredCount: tagResponses.length
      };
    }).filter(assessment => assessment.questionCount > 0);
  }

  /**
   * Calculate analysis for tag-based questions
   */
  private calculateTagAnalysis(questions: Question[], responses: Response[]): MaturityAnalysis {
    // Group questions by dimension for scoring
    const dimensionScores = this.calculateDimensionScores(questions, responses);

    return {
      dimensionScores,
      strengths: this.identifyStrengths(dimensionScores),
      weaknesses: this.identifyWeaknesses(dimensionScores),
      recommendations: this.generateRecommendations(dimensionScores, questions)
    };
  }

  /**
   * Calculate scores by dimension for tag-based questions
   */
  private calculateDimensionScores(questions: Question[], responses: Response[]): Array<{
    dimension: string;
    score: number;
    maxScore: number;
    percentage: number;
    questionCount: number;
    answeredCount: number;
  }> {
    const dimensionMap = new Map<string, Question[]>();

    // Group questions by dimension
    questions.forEach(q => {
      const dimName = q.dimension?.name || 'Other';
      if (!dimensionMap.has(dimName)) {
        dimensionMap.set(dimName, []);
      }
      dimensionMap.get(dimName)!.push(q);
    });

    return Array.from(dimensionMap.entries()).map(([dimension, dimensionQuestions]) => {
      const dimensionResponses = responses.filter(r =>
        dimensionQuestions.some(q => q.id === r.questionId)
      );

      let totalScore = 0;
      let maxPossibleScore = 0;
      let answeredCount = 0;

      dimensionQuestions.forEach(question => {
        const response = dimensionResponses.find(r => r.questionId === question.id);
        const questionScore = this.calculateQuestionScore(question, response);
        const maxQuestionScore = this.getMaxQuestionScore(question);

        totalScore += questionScore;
        maxPossibleScore += maxQuestionScore;

        if (response && response.answerValue) {
          answeredCount++;
        }
      });

      const percentage = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0;

      return {
        dimension,
        score: totalScore,
        maxScore: maxPossibleScore,
        percentage: Math.round(percentage * 100) / 100,
        questionCount: dimensionQuestions.length,
        answeredCount
      };
    });
  }

  /**
   * Calculate score for a single question
   */
  private calculateQuestionScore(question: Question, response?: Response): number {
    if (!response || !response.answerValue) {
      return 0;
    }

    // If it's an AI question, prioritize response.score and do NOT use aiScore (deprecated)
    if (question.isAiQuestion && response.score !== null && response.score !== undefined) {
      return typeof response.score === 'number' ? response.score : parseFloat(response.score.toString());
    }

    // If question has a score in the response, use it
    if (response.score !== null && response.score !== undefined) {
      return typeof response.score === 'number' ? response.score : parseFloat(response.score.toString());
    }

    // Calculate based on question type and scoring rules (from scoringRuleEntries)
    const scoringRules = getScoringRulesMap(question);
    if (Object.keys(scoringRules).length > 0) {
      return scoringRules[response.answerValue] || 0;
    }

    // Default scoring for different question types
    if (question.questionType === 'binary') {
      return response.answerValue === 'Yes' ? 1 : 0;
    }

    if (question.questionType === 'scale') {
      return this.calculateScaleScore(response.answerValue);
    }

    return 0;
  }

  /**
   * Calculate scale score (0-4 scale)
   */
  private calculateScaleScore(answerValue: string): number {
    const scaleMap: Record<string, number> = {
      'Never': 0,
      'Rarely': 1,
      'Sometimes': 2,
      'Often': 3,
      'Always': 4
    };
    return scaleMap[answerValue] || 0;
  }

  /**
   * Get max possible score for a question
   */
  private getMaxQuestionScore(question: Question): number {
    const scoringRules = getScoringRulesMap(question);
    if (Object.keys(scoringRules).length > 0) {
      return Math.max(...Object.values(scoringRules), 4);
    }

    if (question.questionType === 'binary') {
      return 1;
    }

    if (question.questionType === 'scale') {
      return 4;
    }

    return 4; // Default max score
  }

  /**
   * Identify strengths (dimensions above 70%)
   */
  private identifyStrengths(dimensionScores: Array<{ dimension: string; percentage: number }>): string[] {
    return dimensionScores
      .filter(d => d.percentage >= 70)
      .map(d => d.dimension);
  }

  /**
   * Identify weaknesses (dimensions below 40%)
   */
  private identifyWeaknesses(dimensionScores: Array<{ dimension: string; percentage: number }>): string[] {
    return dimensionScores
      .filter(d => d.percentage < 40)
      .map(d => d.dimension);
  }

  /**
   * Generate recommendations based on tag assessment
   */
  private generateRecommendations(
    dimensionScores: Array<{ dimension: string; percentage: number }>,
    questions: Question[]
  ): string[] {
    const recommendations: string[] = [];
    // Add bounds checking before accessing array elements
    if (dimensionScores.length === 0 || questions.length === 0) {
      return recommendations;
    }
    // Extract tag after bounds check to ensure questions[0] exists
    const tag = questions[0]?.tags?.find(t => t.toLowerCase().includes('ai')) || 'AI';

    // Find lowest scoring dimensions
    const sortedScores = [...dimensionScores].sort((a, b) => a.percentage - b.percentage);
    const lowestDimensions = sortedScores.slice(0, 2);

    lowestDimensions.forEach(dimension => {
      if (dimension.percentage < 50) {
        if (tag.toLowerCase() === 'ai') {
          recommendations.push(
            `Improve ${dimension} capabilities related to AI implementation and governance.`
          );
        } else {
          recommendations.push(
            `Focus on strengthening ${dimension} to improve ${tag} readiness.`
          );
        }
      }
    });

    // Overall recommendation
    let avgScore = 0;
    if (dimensionScores.length > 0) {
      avgScore = dimensionScores.reduce((sum, d) => sum + d.percentage, 0) / dimensionScores.length;
    }
    if (avgScore < 50) {
      recommendations.push(
        tag.toLowerCase() === 'ai'
          ? 'Consider a comprehensive AI readiness assessment and strategy development.'
          : `Develop a strategic plan to improve ${tag} capabilities across all dimensions.`
      );
    }

    return recommendations.length > 0 ? recommendations : [
      tag.toLowerCase() === 'ai'
        ? 'Continue building on your AI readiness foundation.'
        : `Maintain strong ${tag} capabilities and continue improvement.`
    ];
  }

  /**
   * Get display name for a tag
   */
  private getDisplayName(tag: string): string {
    const displayNames: Record<string, string> = {
      'AI': 'AI Readiness',
      'ai': 'AI Readiness',
      'security': 'Security Posture',
      'compliance': 'Compliance Readiness',
      'data': 'Data Maturity'
    };
    return displayNames[tag.toLowerCase()] || `${tag} Assessment`;
  }

  /**
   * Get description for a tag
   */
  private getDescription(tag: string): string {
    const descriptions: Record<string, string> = {
      'AI': 'Assessment of your organization\'s readiness and maturity in artificial intelligence capabilities',
      'ai': 'Assessment of your organization\'s readiness and maturity in artificial intelligence capabilities',
      'security': 'Evaluation of your organization\'s security practices and posture',
      'compliance': 'Assessment of compliance readiness and regulatory alignment',
      'data': 'Evaluation of data management and analytics capabilities'
    };
    return descriptions[tag.toLowerCase()] || `Assessment of ${tag} capabilities`;
  }
}

export const tagBasedScoringService = new TagBasedScoringService();

